// Sends step response images + flight log configuration to Claude for PID/filter tuning advice,
// using the Anthropic API key/model configured under Settings -> AI Analysis Settings.
// Ported from https://github.com/bph838/rotorflight-blackbox-bellsandwhistles (js/tuning_ai.js).
// `dangerouslyAllowBrowser: true` is Anthropic's supported bring-your-own-key browser pattern
// (the SDK sets `anthropic-dangerous-direct-browser-access`) - the user supplies their own key,
// stored locally (see stores/settings.js), never sent anywhere but api.anthropic.com.

import Anthropic from "@anthropic-ai/sdk";
import AI_MODELS from "./data/ai_models.json";
import { sliceToPromptText, sliceToSummaryText } from "./flight_slice.js";
import { configChanges, findSameConfigAsPrevious } from "./tuning_log.js";

const MODELS_BY_ID = {};
for (const m of AI_MODELS.models) {
  MODELS_BY_ID[m.id] = m;
}

export const DEFAULT_MODEL = AI_MODELS.defaultModel;

// Models that accept the server-side refusal fallback ("default" routing)
const REFUSAL_FALLBACK_MODELS = new Set(["claude-fable-5-1", "claude-opus-5-5", "claude-opus-5", "claude-sonnet-5-5"]);

function createClient(apiKey) {
  return new Anthropic({
    apiKey,
    dangerouslyAllowBrowser: true,
  });
}

function imageBase64FromDataUrl(dataUrl) {
  return (dataUrl || "").replace(/^data:image\/\w+;base64,/, "");
}

function mediaTypeFromDataUrl(dataUrl) {
  const match = /^data:(image\/\w+);base64,/.exec(dataUrl || "");
  return match ? match[1] : "image/png";
}

function imageBlocksFromDataUrls(dataUrls) {
  return (dataUrls || []).map((dataUrl) => ({
    type: "image",
    source: { type: "base64", media_type: mediaTypeFromDataUrl(dataUrl), data: imageBase64FromDataUrl(dataUrl) },
  }));
}

/**
 * Estimated cost in USD for one API response, from its `usage` object. Cache writes are priced at
 * the 5-minute-TTL premium (1.25x input) since that's what this app requests. Cache reads are
 * 0.1x input unless the model lists its own pricePerMillionCacheReadTokens (some are cheaper).
 */
export function estimateCostUsd(model, usage) {
  const pricing = MODELS_BY_ID[model];
  if (!pricing || !usage) return 0;

  const cacheReadPrice = pricing.pricePerMillionCacheReadTokens ?? pricing.pricePerMillionInputTokens * 0.1;

  let cost = 0;
  cost += ((usage.input_tokens || 0) / 1e6) * pricing.pricePerMillionInputTokens;
  cost += ((usage.output_tokens || 0) / 1e6) * pricing.pricePerMillionOutputTokens;
  cost += ((usage.cache_creation_input_tokens || 0) / 1e6) * pricing.pricePerMillionInputTokens * 1.25;
  cost += ((usage.cache_read_input_tokens || 0) / 1e6) * cacheReadPrice;

  return cost;
}

/**
 * What one API response used, in the shape kept on a tuning log entry (see tuning_log.js addUsage):
 * { requests, inputTokens, outputTokens, cacheReadTokens, cacheWriteTokens, codeSteps }.
 * inputTokens is only the uncached part; codeSteps counts the code-execution calls Claude made
 * running skills - each one re-reads the whole prompt, which is where most of a skills request's
 * cost goes.
 */
export function summariseUsage(response) {
  const usage = (response && response.usage) || {};
  const codeSteps = ((response && response.content) || []).filter((block) => block.type === "server_tool_use").length;
  return {
    requests: 1,
    inputTokens: usage.input_tokens || 0,
    outputTokens: usage.output_tokens || 0,
    cacheReadTokens: usage.cache_read_input_tokens || 0,
    cacheWriteTokens: usage.cache_creation_input_tokens || 0,
    codeSteps,
  };
}

const NO_INSTRUCTIONS_PLACEHOLDER = "(No specific instructions given - provide general tuning suggestions.)";
const INSTRUCTIONS_START = "User instructions: ";
const INSTRUCTIONS_END = "\n\nAnalyse the attached step response graph";

/**
 * Pulls back out what the user actually typed from a prompt built by buildPromptText(), so the
 * initial question can be shown in the conversation without the surrounding boilerplate/config.
 * Returns "" when no instructions were given.
 */
export function extractInstructions(promptText) {
  const text = String(promptText ?? "");
  const start = text.indexOf(INSTRUCTIONS_START);
  const end = text.lastIndexOf(INSTRUCTIONS_END);
  if (start === -1 || end === -1 || end < start) return "";

  const instructions = text.slice(start + INSTRUCTIONS_START.length, end);
  return instructions === NO_INSTRUCTIONS_PLACEHOLDER ? "" : instructions;
}

const NOTES_START =
  "The user's notes for this flight (e.g. what they changed since the last flight, and what they're trying to fix):\n";

const NOTES_UPDATED_START = "The user has updated their notes for this flight:\n";

/** True for the text block ask() sends updated notes in - shown in the UI as the Notes box, not as part of the question. */
export function isNotesUpdateText(text) {
  return typeof text === "string" && text.startsWith(NOTES_UPDATED_START);
}

/**
 * True when the user's notes for an entry (trimmed) have already been sent somewhere in its
 * conversation - so a follow-up only needs to send them again if they've been edited since.
 */
export function notesAlreadySent(conversation, notes) {
  const trimmed = (notes || "").trim();
  if (!trimmed) return true;
  return (conversation || []).some((turn) => {
    if (turn.role !== "user") return false;
    const blocks = typeof turn.content === "string" ? [{ type: "text", text: turn.content }] : turn.content || [];
    return blocks.some((block) => block.type === "text" && block.text.includes(trimmed));
  });
}

/**
 * options: { configSummary, instructions, expertMode, hasSlice, heliDescription, notes }
 * notes: what the user wrote in this entry's Notes box.
 * hasSlice: an isolated-flight CSV block (see flight_slice.js) is attached to the same message.
 * heliDescription: the user's own description of the heli (size, make/model, etc.) from the
 * tuning log, if they gave one.
 */
export function buildPromptText(options) {
  const instructions = (options.instructions || "").trim() || NO_INSTRUCTIONS_PLACEHOLDER;
  const heliDescription = (options.heliDescription || "").trim();

  let text =
    "You are helping tune the PID controller of an RC helicopter flight controller running Rotorflight " +
    "(forked from Betaflight). Attached is a step response graph generated from a blackbox log, showing " +
    "setpoint-vs-gyro tracking (Roll in red, Pitch in cyan, Yaw in yellow) for the 0-500ms period after a " +
    "stick input, with 1.0 on the y-axis representing perfect tracking.\n\n";

  if (heliDescription) {
    text +=
      "The user describes the helicopter as follows - take its size, build and components into account " +
      `when judging the response and recommending values:\n${heliDescription}\n\n`;
  }

  text += `Current flight controller configuration extracted from the log:\n${options.configSummary}\n\n`;

  const notes = (options.notes || "").trim();
  if (notes) {
    text += `${NOTES_START}${notes}\n\n`;
  }

  text +=
    `${INSTRUCTIONS_START}${instructions}${INSTRUCTIONS_END}` +
    "Analyse the attached step response graph and suggest specific, actionable PID changes " +
    "to address the user's instructions, referencing the actual curve shapes you see (overshoot, " +
    "settling time, oscillation, delay) for each axis.\n\n";

  if (options.hasSlice) {
    text +=
      "Also attached is CSV data for a block of the flight the user isolated, with the fields they " +
      "were looking at. Use it alongside the step response graph - e.g. compare setpoint against gyro " +
      "for tracking error, overshoot and bounce-back on real stick inputs, and look for oscillation, " +
      "I-term windup or saturation - and cite times (t_ms) from it where it supports a recommendation.\n\n";
  }

  if (options.expertMode) {
    text +=
      "Expert mode is enabled: beyond PID and filter changes, also review the rest of the configuration " +
      "above for other settings worth adjusting - rate profiles, feedforward, TPA (throttle PID " +
      "attenuation), I-term relax, RPM filtering, governor/collective settings, voltage sag " +
      "compensation, mixer settings, and anything else that looks misconfigured or suboptimal given the " +
      "behaviour shown in the step response. Flag these in addition to, not instead of, any PID/filter " +
      "changes.\n\n";
  }

  text +=
    "For every change you recommend, clearly state which setting to change in the Rotorflight Configurator " +
    "and where to find it, using this format: the exact field name as it appears in the Configurator UI, " +
    "the tab/page it lives on (e.g. PID Tuning, Filters, Rates), the current value (from the configuration " +
    "above), and the new value you recommend. Do not just describe the change conceptually - name the " +
    "actual Configurator setting for the user to go and edit.\n\n" +
    "Present any PID gain changes grouped by axis in this order: Roll, then Pitch, then Yaw, and within " +
    "each axis give the gains in this order: P, I, D.\n\n" +
    "If earlier messages above contain step response graphs, configuration and analysis from previous " +
    "entries in this tuning log, use that history to track what has already been tried and how the " +
    "response changed as a result, rather than repeating suggestions that were already applied unless " +
    "they still need further adjustment.";

  return text;
}

/**
 * previousConfig: the config of the history entry sent just before this one, or null for the
 * first - every later entry only lists the settings that changed since, which is most of what
 * keeps a long history small (a full config is a few thousand characters, and rarely more than a
 * few lines change between flights).
 */
function entryToHistoryContent(entry, includeImage, previousConfig) {
  const content = [];

  if (includeImage && entry.image) {
    content.push({
      type: "image",
      source: { type: "base64", media_type: "image/png", data: imageBase64FromDataUrl(entry.image) },
    });
  }

  let text = `Step response captured ${entry.timestamp}`;
  if (!includeImage && entry.image) {
    text += " (graph omitted to save tokens - see the configuration and earlier analysis below)";
  }
  if (previousConfig && entry.config) {
    const changes = configChanges(previousConfig, entry.config);
    text += changes.length
      ? `\n\nConfiguration changes since the previous entry (every other setting unchanged):\n${changes.join("\n")}`
      : "\n\nConfiguration: unchanged since the previous entry.";
  } else {
    text += `\n\nConfiguration:\n${entry.config || "(none)"}`;
  }
  if (entry.notes) {
    text += `\n\nUser notes: ${entry.notes}`;
  }
  if (entry.slice) {
    text += `\n\n${sliceToSummaryText(entry.slice)}`;
  }

  content.push({ type: "text", text });

  return content;
}

function lastAssistantText(entry) {
  const conversation = (entry.ai && entry.ai.conversation) || [];

  for (let i = conversation.length - 1; i >= 0; i--) {
    if (conversation[i].role === "assistant") {
      return typeof conversation[i].content === "string" ? conversation[i].content : null;
    }
  }

  return null;
}

/**
 * Turns every entry already in the log into a user/assistant message pair (image + config/notes,
 * plus that entry's own final AI answer if it has one), so a new request has the whole tuning
 * log's history as context. Pass excludingEntryId to leave out the entry currently being asked
 * about (it's supplied separately as the new message, not as history).
 *
 * To save tokens, an entry flown on the same config as the entry before it is left out - it adds
 * nothing the AI hasn't already seen - unless it has its own AI conversation, which the AI needs
 * to follow what it has already advised, or notes the user has written about that flight.
 *
 * maxImages limits how many step response graphs the history carries, to save tokens: only the
 * most recent history entries keep their image - at most maxImages, at times a few fewer - older
 * ones are sent as text only (config changes, notes, earlier analysis). null/undefined or a
 * negative value sends every image.
 */
export function buildHistoryMessages(log, excludingEntryId, maxImages) {
  const messages = [];
  const allEntries = (log && log.entries) || [];
  const sameConfigAsPrevious = findSameConfigAsPrevious(allEntries);
  const entries = allEntries.filter(
    (entry) =>
      !(excludingEntryId && entry.id === excludingEntryId) &&
      !(sameConfigAsPrevious.has(entry.id) && !entry.ai?.conversation?.length && !entry.notes?.trim()),
  );

  const sendAllImages = maxImages === null || maxImages === undefined || maxImages < 0;
  // Older images are dropped a few at a time rather than one per new entry: the request is cached
  // (see streamClaude), and dropping an image changes the history from that entry on - so a
  // window sliding by one would make every new entry pay full price for the last maxImages graphs
  // again. Stepped, the history is unchanged apart from what's added, for several entries running.
  const imageStep = Math.max(1, Math.min(5, Math.ceil(maxImages / 2)));
  const firstImageIndex = sendAllImages
    ? 0
    : Math.ceil(Math.max(0, entries.length - maxImages) / imageStep) * imageStep;

  entries.forEach((entry, index) => {
    const previousConfig = index > 0 ? entries[index - 1].config : null;
    messages.push({ role: "user", content: entryToHistoryContent(entry, index >= firstImageIndex, previousConfig) });

    const assistantText = lastAssistantText(entry);
    if (assistantText) {
      messages.push({ role: "assistant", content: assistantText });
    }
  });

  return messages;
}

/**
 * Streams one request to Claude and hands back the complete final message - every content block
 * (text, thinking, tool_use, ...) exactly as the API returned it. Shared by the Tuning Log (via
 * sendMessages, which keeps only the text) and Imagine's AI Filters panel (which needs tool_use
 * blocks for structured suggestions, and must send thinking blocks back unchanged on the next
 * turn).
 *
 * options: { apiKey, model, effort, skillIds, onChunk(textSnapshot), onStatus(status) }
 * extras: { system, tools } - optional system prompt (string or text blocks) and client tools
 * handlers: { onFinal(response, costUsd, usage), onError(message) }
 *
 * Returns a cancel() function that aborts the request - after which neither onFinal nor onError
 * is called (the caller already knows it cancelled).
 */
export function streamClaude(options, extras, messages, handlers) {
  const { onFinal, onError } = handlers;
  let client;
  try {
    client = createClient(options.apiKey);
  } catch (e) {
    onError(`Could not load the Anthropic SDK: ${e.message}`);
    return NO_CANCEL;
  }

  const model = options.model || DEFAULT_MODEL;
  const requestParams = {
    model,
    // Adaptive thinking counts against this same budget, and models like Opus can spend most or
    // all of a small budget on thinking before writing any answer - leaving too little here is
    // what previously caused empty/cut-off responses. Streaming (below) means we're not racing a
    // client-side HTTP timeout to get the full response back.
    max_tokens: 16000,
    messages,
    // Caches the whole prompt (history graphs, config, conversation) for 5 minutes. Follow-up
    // questions and retries then read it back at a tenth of the price or less - and so does every
    // code-execution step within a request when skills are on, each of which re-reads the prompt.
    cache_control: { type: "ephemeral" },
  };

  if (extras && extras.system) {
    requestParams.system = extras.system;
  }
  if (extras && extras.tools && extras.tools.length) {
    requestParams.tools = [...extras.tools];
  }

  // Adaptive thinking isn't supported on every model (e.g. Haiku 4.5) - only request it where it's valid
  const modelInfo = MODELS_BY_ID[model];
  if (!modelInfo || modelInfo.supportsAdaptiveThinking) {
    requestParams.thinking = { type: "adaptive" };
  }

  // Effort isn't supported on every model (e.g. Haiku 4.5, which errors if it's sent) - only
  // request it where it's valid
  if (options.effort && (!modelInfo || modelInfo.supportsEffort)) {
    requestParams.output_config = { effort: options.effort };
  }

  // Custom Agent Skills (uploaded separately to the Anthropic account via the Skills API/
  // Console, identified by their skill_id) are loaded into a code-execution container - they
  // aren't picked up automatically just because they exist on the account.
  const skillIds = (options.skillIds || []).filter(Boolean);
  if (skillIds.length > 0) {
    requestParams.tools = [...(requestParams.tools || []), { type: "code_execution_20260521", name: "code_execution" }];
    requestParams.container = {
      skills: skillIds.map((skillId) => ({ type: "custom", skill_id: skillId, version: "latest" })),
    };
    requestParams.betas = ["code-execution-2025-08-25", "skills-2025-10-02"];
    // This app only renders the model's text blocks - there's no file transfer or download
    // mechanism, so any file the code-execution container writes is unreachable and effectively
    // lost. Skills must still answer entirely in chat text rather than treating a written file as
    // their deliverable.
    const skillsNote =
      "This is a chat-only interface with no file download or transfer capability - any file " +
      "written to the code execution container is permanently inaccessible to the user. Never " +
      "treat a saved/exported file as your deliverable. Always give your complete answer directly " +
      "as chat text/markdown in your response, even if that means writing out a long answer " +
      "instead of a file.";
    if (!requestParams.system) {
      requestParams.system = skillsNote;
    } else {
      const base =
        typeof requestParams.system === "string" ? [{ type: "text", text: requestParams.system }] : requestParams.system;
      requestParams.system = [...base, { type: "text", text: skillsNote }];
    }
  }

  // Server-side refusal fallback: if this model's safeguards decline the request, the API re-runs
  // it on a suitable fallback model inside the same call instead of returning a refusal.
  if (REFUSAL_FALLBACK_MODELS.has(model)) {
    requestParams.betas = [...(requestParams.betas || []), "server-side-fallback-2026-07-01"];
    requestParams.fallbacks = "default";
  }

  // The SDK can't report how much of the request has been uploaded, so progress is reported as
  // stages instead: sending (with the request's size), then the model thinking, then writing.
  const onStatus = typeof options.onStatus === "function" ? options.onStatus : () => {};
  const requestBytes = new TextEncoder().encode(JSON.stringify(requestParams)).length;
  onStatus({ phase: "sending", requestBytes });

  let stream;
  try {
    stream = client.beta.messages.stream(requestParams);
  } catch (e) {
    onError(e && e.message ? e.message : String(e));
    return NO_CANCEL;
  }

  // The response starts once the whole request has arrived; until the first text, the model is
  // thinking (or running skills' code)
  stream.on("connect", () => {
    if (!cancelled) onStatus({ phase: "thinking", requestBytes });
  });
  stream.on("text", () => {
    if (!cancelled) onStatus({ phase: "writing", requestBytes });
  });

  let cancelled = false;

  if (typeof options.onChunk === "function") {
    stream.on("text", (textDelta, textSnapshot) => {
      if (!cancelled) options.onChunk(textSnapshot);
    });
  }

  stream.on("finalMessage", (response) => {
    if (cancelled) return;
    onFinal(response, estimateCostUsd(model, response.usage), summariseUsage(response));
  });

  stream.on("error", (error) => {
    if (!cancelled) onError(error && error.message ? error.message : String(error));
  });

  // stream.abort() emits "abort" rather than "error" - nothing to report, the caller cancelled
  stream.on("abort", () => {});

  return function cancel() {
    if (cancelled) return;
    cancelled = true;
    stream.abort();
  };
}

/** All text blocks of a response joined, with a note appended if it was cut off. */
export function responseText(response) {
  let text = "";
  for (const block of (response && response.content) || []) {
    if (block.type === "text") {
      text += block.text;
    }
  }

  if (response && response.stop_reason === "max_tokens") {
    text = `${text ? `${text}\n\n` : ""}_(This response was cut off - it ran out of its token budget, often because most of it was spent on internal reasoning before any answer was written. Try again, or ask a more focused follow-up question.)_`;
  }
  return text;
}

/**
 * The Tuning Log's request: streams via streamClaude() and keeps only the reply's text in the
 * conversation. options may include an `onChunk(textSnapshot)` callback - if given, it's called
 * every time more response text arrives, with the full text accumulated so far (not just the
 * latest delta), so callers can render progressively instead of waiting for the whole response.
 *
 * Returns a cancel() function that aborts the request - after which neither onResult nor onError
 * is called (the caller already knows it cancelled).
 */
function sendMessages(options, messages, onResult, onError) {
  return streamClaude(options, null, messages, {
    onFinal(response, costUsd, usage) {
      const text = responseText(response) || "(No text response received)";
      const updatedMessages = messages.concat([{ role: "assistant", content: text }]);
      onResult(text, updatedMessages, costUsd, usage);
    },
    onError,
  });
}

const NO_CANCEL = () => {};

/**
 * Starts a new tuning-advice conversation about a single entry (its image + config summary), with
 * the rest of the tuning log's history prepended as context.
 *
 * options: { apiKey, model, effort, skillIds, historyMessages, entry: {image,
 * config}, instructions, expertMode, heliDescription, images, slice, onChunk }
 * slice, if given, is an isolated-flight slice (see flight_slice.js) sent as a CSV text block.
 * images, if given, is an array of extra data-URL images (e.g. pasted screenshots) to attach
 * alongside the entry's own step response image.
 * effort, if given, is passed through as output_config.effort ('low'/'medium'/'high'/'xhigh'/
 * 'max') on models that support it - ignored otherwise.
 * skillIds, if given, are the IDs of custom Agent Skills previously uploaded to this Anthropic
 * account (via the Console or Skills API) - they're all loaded into a shared code-execution
 * container for this request, at version 'latest'.
 * onChunk(textSnapshot), if given, is called repeatedly as the response streams in, with the full
 * response text accumulated so far - use it to render progressively instead of waiting for the
 * whole answer.
 * onResult(resultText, entryMessages, costUsd, usage) - usage is summariseUsage() for this call; entryMessages is *this entry's own* conversation
 * (not including historyMessages/repeats of it) - keep it and pass it back into ask() for
 * follow-ups, and persist it as entry.ai.conversation. costUsd is this call's estimated price -
 * add it to any running total you're keeping for the entry.
 *
 * Returns a cancel() function that aborts the request (see sendMessages).
 */
export function analyze(options, onResult, onError) {
  if (!options.apiKey) {
    onError("No Anthropic API key configured. Add one under Settings → AI Analysis Settings.");
    return NO_CANCEL;
  }

  const historyMessages = options.historyMessages || [];
  const content = [];

  if (options.entry.image) {
    content.push({
      type: "image",
      source: { type: "base64", media_type: "image/png", data: imageBase64FromDataUrl(options.entry.image) },
    });
  }

  content.push(...imageBlocksFromDataUrls(options.images));

  if (options.slice) {
    content.push({ type: "text", text: sliceToPromptText(options.slice) });
  }

  content.push({
    type: "text",
    text: buildPromptText({
      configSummary: options.entry.config,
      instructions: options.instructions,
      expertMode: options.expertMode,
      hasSlice: !!options.slice,
      heliDescription: options.heliDescription,
      notes: options.entry.notes,
    }),
  });

  const initialMessage = { role: "user", content };

  return sendMessages(
    options,
    historyMessages.concat([initialMessage]),
    (text, updatedMessages, costUsd, usage) => {
      onResult(text, updatedMessages.slice(historyMessages.length), costUsd, usage);
    },
    onError,
  );
}

/**
 * Continues an existing entry's conversation with a follow-up question.
 *
 * options: { apiKey, model, effort, skillIds, historyMessages, messages, question,
 * images, slice, notes, onChunk }
 * notes, if given, is the entry's current Notes text - sent along if it isn't already in the
 * conversation (written or edited since it started).
 * slice, if given, is an isolated-flight slice (see flight_slice.js) sent as a CSV text block.
 * `messages` is this entry's own conversation so far (as returned by a previous analyze()/ask() call).
 * effort, if given, is passed through as output_config.effort on models that support it.
 * skillIds behaves as documented on analyze().
 * images, if given, is an array of extra data-URL images (e.g. pasted screenshots) to attach
 * alongside the question.
 * onChunk(textSnapshot), if given, is called repeatedly as the response streams in, with the full
 * response text accumulated so far.
 * onResult(resultText, entryMessages, costUsd, usage) - usage is summariseUsage() for this call; pass the updated entryMessages back in for the
 * next follow-up; costUsd is this call's estimated price.
 *
 * Returns a cancel() function that aborts the request (see sendMessages).
 */
export function ask(options, onResult, onError) {
  if (!options.apiKey) {
    onError("No Anthropic API key configured. Add one under Settings → AI Analysis Settings.");
    return NO_CANCEL;
  }

  const question = (options.question || "").trim();
  const imageBlocks = imageBlocksFromDataUrls(options.images);
  const sliceBlocks = options.slice ? [{ type: "text", text: sliceToPromptText(options.slice) }] : [];
  // Notes written or edited since the conversation started go along with the next message
  const notes = (options.notes || "").trim();
  const notesBlocks = notesAlreadySent(options.messages, notes)
    ? []
    : [{ type: "text", text: `${NOTES_UPDATED_START}${notes}` }];
  if (!question && !imageBlocks.length && !sliceBlocks.length) {
    onError("Please enter a question.");
    return NO_CANCEL;
  }

  // Plain text when there's nothing attached, keeping the stored conversation shape unchanged for
  // the common case - an array of blocks only when there's something to attach.
  const content =
    imageBlocks.length || sliceBlocks.length || notesBlocks.length
      ? [
          ...imageBlocks,
          ...sliceBlocks,
          ...notesBlocks,
          { type: "text", text: question || "(No question given - see the attached data.)" },
        ]
      : question;

  const historyMessages = options.historyMessages || [];
  const messages = (options.messages || []).concat([{ role: "user", content }]);

  return sendMessages(
    options,
    historyMessages.concat(messages),
    (text, updatedMessages, costUsd, usage) => {
      onResult(text, updatedMessages.slice(historyMessages.length), costUsd, usage);
    },
    onError,
  );
}
